import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import FillableFormFiller from "@/components/FillableFormFiller";
import IdentityDocsStep from "@/components/IdentityDocsStep";
import OrientationDocCard from "@/components/orientation/OrientationDocCard";
import { SignaturePad } from "@/components/orientation/SignaturePad";
import {
  Video,
  FileText,
  ListChecks,
  CheckCircle,
  Loader2,
  Trophy,
  ClipboardCheck,
  IdCard,
  GraduationCap,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { SignaturePad };

function VideoPlayer({ video, token, onWatched }) {
  const videoRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (video.source_type !== "upload") return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(video.video_url.startsWith("/api") ? `${BACKEND_URL}${video.video_url}` : video.video_url);
        const blob = await res.blob();
        if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id]);

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    if (pct >= 90) onWatched();
  };

  if (video.source_type === "url") {
    const isEmbed = /youtube\.com|vimeo\.com|youtu\.be/.test(video.url || "");
    return (
      <div className="space-y-2">
        {isEmbed ? (
          <iframe
            src={video.url}
            title={video.title}
            className="w-full aspect-video rounded-lg border border-slate-200"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video
            src={video.url}
            controls
            className="w-full rounded-lg"
            onTimeUpdate={handleTimeUpdate}
            ref={videoRef}
          />
        )}
        <Button size="sm" variant="outline" onClick={onWatched} className="gap-1">
          <CheckCircle className="w-4 h-4" /> Mark as watched
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {loading || !blobUrl ? (
        <div className="w-full aspect-video rounded-lg bg-slate-100 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <video
          ref={videoRef}
          src={blobUrl}
          controls
          className="w-full rounded-lg"
          onTimeUpdate={handleTimeUpdate}
        />
      )}
      <Button size="sm" variant="outline" onClick={onWatched} className="gap-1">
        <CheckCircle className="w-4 h-4" /> Mark as watched
      </Button>
    </div>
  );
}

// One in-service may have a video, a PDF to fill & sign, or both; complete when every part is done.
function groupInservices(kit, videosDone, docsSigned) {
  const byId = new Map();
  kit.videos.filter((v) => v.kind === "inservice").forEach((v) => byId.set(v.id, { ...v, video: v }));
  kit.documents.filter((d) => d.kind === "inservice").forEach((d) => byId.set(d.id, { ...(byId.get(d.id) || d), doc: d }));
  return [...byId.values()].map((i) => ({
    ...i,
    complete: (!i.video || (videosDone[i.id]?.percent || 0) >= 90) && (!i.doc || !!docsSigned[i.id]?.signed_at),
  }));
}

export default function OrientationPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerNames, setSignerNames] = useState({});
  const [signatures, setSignatures] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [formValues, setFormValues] = useState({}); // {docId: {fieldId: value}} for fillable docs
  const [fillingDoc, setFillingDoc] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orientation/${token}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to load orientation");
    } finally {
      setLoading(false);
    }
  };

  const markVideoWatched = async (videoId) => {
    try {
      const res = await axios.post(`${API}/orientation/${token}/video/${videoId}/watched`, {
        percent: 100,
      });
      setData((d) => ({ ...d, progress: res.data.progress, progress_summary: res.data.progress_summary, status: res.data.status, completed_at: res.data.completed_at }));
    } catch (err) {
      toast.error("Could not save progress");
    }
  };

  const toggleChecklist = async (itemId, currentlyChecked) => {
    try {
      const res = await axios.post(
        `${API}/orientation/${token}/checklist/${itemId}/toggle?checked=${!currentlyChecked}`
      );
      setData((d) => ({ ...d, progress: res.data.progress, progress_summary: res.data.progress_summary, status: res.data.status, completed_at: res.data.completed_at }));
    } catch (err) {
      toast.error("Could not update checklist");
    }
  };

  const signDocument = async (doc, ackOnly) => {
    const signer = signerNames[doc.id];
    if (!signer) {
      toast.error("Please type your full name to sign");
      return;
    }
    if (!ackOnly && !signatures[doc.id]) {
      toast.error("Please draw your signature");
      return;
    }
    const isFillable = !!doc.fillable_form?.enabled && (doc.fillable_form.fields || []).length > 0;
    if (isFillable && !formValues[doc.id]) {
      toast.error("Please fill out the form first");
      return;
    }
    setSavingId(doc.id);
    try {
      const res = await axios.post(`${API}/orientation/${token}/document/${doc.id}/sign`, {
        signer_name: signer,
        signature_data_url: ackOnly ? null : signatures[doc.id],
        ack_only: ackOnly,
        values: isFillable ? formValues[doc.id] : undefined,
      });
      setData((d) => ({ ...d, progress: res.data.progress, progress_summary: res.data.progress_summary, status: res.data.status, completed_at: res.data.completed_at }));
      toast.success(ackOnly ? "Acknowledged" : "Signed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sign");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white p-8 rounded-lg border border-slate-200 max-w-md text-center">
          <h1 className="text-lg font-semibold text-slate-900">Link unavailable</h1>
          <p className="text-slate-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const s = data.progress_summary || { percent: 0, done: 0, total: 0, is_complete: false };
  const videosDone = data.progress?.videos || {};
  const docsSigned = data.progress?.documents || {};
  const checkDone = data.progress?.checklist || {};
  const identityDocs = data.kit.identity_documents || [];
  const regularDocs = data.kit.documents.filter((d) => d.kind !== "inservice");
  const inservices = groupInservices(data.kit, videosDone, docsSigned);

  const refreshSummary = async () => {
    try {
      const res = await axios.get(`${API}/orientation/${token}`);
      setData((d) => ({ ...d, progress: res.data.progress, progress_summary: res.data.progress_summary, status: res.data.status, completed_at: res.data.completed_at }));
    } catch (err) { /* keep current view */ }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">
            {data.agency?.name || "Agency Orientation"}
          </p>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="orientation-title">
            {data.kind === "inservice" ? "In-service Training" : "New Hire Orientation"}{data.new_hire_name ? ` — ${data.new_hire_name}` : ""}
          </h1>
          {data.expires_at && <p className="text-xs text-slate-500 mt-1">Please complete by {new Date(data.expires_at).toLocaleDateString()}</p>}
          <div className="flex items-center gap-3 mt-4">
            <Progress value={s.percent} className="h-2 flex-1" />
            <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
              {s.done} / {s.total} ({s.percent}%)
            </span>
          </div>
          {s.is_complete && (
            <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-2" data-testid="orientation-complete-banner">
              <Trophy className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">
                {data.kind === "inservice" ? "All assigned in-service training complete — great work!" : "Orientation complete — welcome aboard!"}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Identity documents */}
        {identityDocs.length > 0 && (
          <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="orientation-identity-section">
            <div className="flex items-center gap-2 mb-4">
              <IdCard className="w-5 h-5 text-teal-600" />
              <h2 className="font-semibold text-slate-900">Identity documents</h2>
              {s.identity_docs && (
                <Badge variant="outline" className="ml-1 text-xs">
                  {s.identity_docs.done}/{s.identity_docs.total}
                </Badge>
              )}
            </div>
            <IdentityDocsStep kind="orientation" token={token} onStatus={refreshSummary} />
          </section>
        )}

        {/* In-service trainings assigned to this employee (video, PDF to fill & sign, or both) */}
        {inservices.length > 0 && (
          <section className="bg-white rounded-xl border border-teal-200 p-6" data-testid="orientation-inservices-section">
            <div className="flex items-center gap-2 mb-1">
              <GraduationCap className="w-5 h-5 text-teal-600" />
              <h2 className="font-semibold text-slate-900">In-service training</h2>
              <Badge variant="outline" className="ml-1 text-xs" data-testid="inservices-progress">
                {inservices.filter((i) => i.complete).length}/{inservices.length}
              </Badge>
            </div>
            <p className="text-sm text-slate-500 mb-4">These trainings were assigned to you by your agency. Watch each video to the end, and fill out & sign any attached form — completed forms are sent to your office automatically.</p>
            <div className="space-y-6">
              {inservices.map((i) => (
                <div key={i.id} className="space-y-2" data-testid={`inservice-item-${i.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{i.title}{i.hours ? <span className="ml-2 text-xs text-slate-400">{i.hours} h</span> : null}</p>
                      {i.description && <p className="text-sm text-slate-500">{i.description}</p>}
                    </div>
                    {i.complete && (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1" data-testid={`inservice-complete-${i.id}`}>
                        <CheckCircle className="w-3 h-3" /> Completed
                      </Badge>
                    )}
                  </div>
                  {i.video && <VideoPlayer video={i.video} token={token} onWatched={() => markVideoWatched(i.id)} />}
                  {i.doc && (
                    <OrientationDocCard
                      doc={i.doc}
                      entry={docsSigned[i.id]}
                      filled={!!formValues[i.id]}
                      signerName={signerNames[i.id]}
                      onSignerName={(v) => setSignerNames((s2) => ({ ...s2, [i.id]: v }))}
                      onSignature={(url) => setSignatures((s3) => ({ ...s3, [i.id]: url }))}
                      onFill={() => setFillingDoc(i.doc)}
                      onSign={(ack) => signDocument(i.doc, ack)}
                      saving={savingId === i.id}
                      showTitle={false}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Videos */}
        {data.kit.videos.some((v) => v.kind !== "inservice") && (
          <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="orientation-videos-section">
            <div className="flex items-center gap-2 mb-4">
              <Video className="w-5 h-5 text-purple-600" />
              <h2 className="font-semibold text-slate-900">Videos</h2>
              <Badge variant="outline" className="ml-1 text-xs">
                {s.videos.done}/{s.videos.total}
              </Badge>
            </div>
            <div className="space-y-6">
              {data.kit.videos.filter((v) => v.kind !== "inservice").map((v) => {
                const watched = (videosDone[v.id]?.percent || 0) >= 90;
                return (
                  <div key={v.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{v.title}</p>
                        {v.description && <p className="text-sm text-slate-500">{v.description}</p>}
                      </div>
                      {watched && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
                          <CheckCircle className="w-3 h-3" /> Watched
                        </Badge>
                      )}
                    </div>
                    <VideoPlayer video={v} token={token} onWatched={() => markVideoWatched(v.id)} />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Documents */}
        {regularDocs.length > 0 && (
          <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="orientation-documents-section">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-blue-600" />
              <h2 className="font-semibold text-slate-900">Documents</h2>
              <Badge variant="outline" className="ml-1 text-xs">
                {regularDocs.filter((d) => docsSigned[d.id]?.signed_at).length}/{regularDocs.length}
              </Badge>
            </div>
            <div className="space-y-6">
              {regularDocs.map((d) => (
                <OrientationDocCard
                  key={d.id}
                  doc={d}
                  entry={docsSigned[d.id]}
                  filled={!!formValues[d.id]}
                  signerName={signerNames[d.id]}
                  onSignerName={(v) => setSignerNames((s2) => ({ ...s2, [d.id]: v }))}
                  onSignature={(url) => setSignatures((s3) => ({ ...s3, [d.id]: url }))}
                  onFill={() => setFillingDoc(d)}
                  onSign={(ack) => signDocument(d, ack)}
                  saving={savingId === d.id}
                />
              ))}
            </div>
          </section>
        )}

        {fillingDoc && (
          <FillableFormFiller
            open
            onClose={() => setFillingDoc(null)}
            form={fillingDoc}
            title={`Fill: ${fillingDoc.title}`}
            pdfEndpoint={`${BACKEND_URL}${fillingDoc.document_url}`}
            submitLabel="Save answers"
            onSubmitValues={async (values) => {
              setFormValues((v) => ({ ...v, [fillingDoc.id]: values }));
              toast.success("Answers saved — now type your name and sign below");
            }}
          />
        )}

        {/* Checklist */}
        {data.kit.checklist.length > 0 && (
          <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="orientation-checklist-section">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-slate-900">Checklist</h2>
              <Badge variant="outline" className="ml-1 text-xs">
                {s.checklist.done}/{s.checklist.total}
              </Badge>
            </div>
            <div className="space-y-3">
              {data.kit.checklist.map((c) => {
                const checked = !!checkDone[c.id]?.checked_at;
                return (
                  <label
                    key={c.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      checked ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleChecklist(c.id, checked)}
                      data-testid={`checklist-${c.id}`}
                    />
                    <div>
                      <p className={`font-medium ${checked ? "text-emerald-800" : "text-slate-900"}`}>
                        {c.title}
                      </p>
                      {c.description && (
                        <p className="text-sm text-slate-500 mt-1">{c.description}</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </section>
        )}

        {data.kit.videos.length === 0 &&
          data.kit.documents.length === 0 &&
          data.kit.checklist.length === 0 &&
          identityDocs.length === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
              <ClipboardCheck className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-600">
                Your agency hasn't set up an orientation kit yet. Please contact your administrator.
              </p>
            </div>
          )}
      </div>
    </div>
  );
}
